import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { LedgerLine } from "../UIToolkit/molecules/LedgerLine";
import { signedMoneyOrHidden } from "./consts";
import { WageSegmentView } from "./WageSegmentView";
import type { WageComposition } from "./wageComposition";

type Props = {
	composition: WageComposition;
	compositionOpen: boolean;
	regionIdOf: (rowId: number) => string;
	agencyName: string | null;
	canViewWage: boolean;
	canOpenWageSettings: boolean;
};

/** Every segment of the shift, closed by a Rounding line when the rounded segment wages miss the total. */
export const WageSegmentList: FC<Props> = ({ composition, compositionOpen, regionIdOf, agencyName, canViewWage, canOpenWageSettings }) => {
	const { t } = useTranslation();
	// Rows that disagree on base get no header base. A segment with captured lines states its base in its
	// first line; one shown as a rate type only has no such line, so it states its own on the segment line.
	const showsOwnBase = (segment: WageComposition["segments"][number]): boolean =>
		composition.sharedBaseCents === undefined && segment.type === "paid" && segment.lines === null;
	return (
		<Box component="ul" aria-label={t("wageBreakdown.segments.ariaLabel")} sx={{ m: 0, px: { xs: 1, sm: 2 }, py: 1 }}>
			{composition.segments.map((segment) => (
				<WageSegmentView
					key={segment.row.id}
					segment={segment}
					compositionOpen={compositionOpen}
					regionId={regionIdOf(segment.row.id)}
					showOwnBase={showsOwnBase(segment)}
					agencyName={agencyName}
					canViewWage={canViewWage}
					canOpenWageSettings={canOpenWageSettings}
				/>
			))}
			{composition.wageRoundingCents !== 0 && (
				<LedgerLine
					variant="adjustment"
					label={t("wageBreakdown.rounding")}
					detail={t("wageBreakdown.roundingDetail")}
					amount={signedMoneyOrHidden(composition.wageRoundingCents, canViewWage)}
					dataTestId="wage-breakdown-wage-rounding"
				/>
			)}
		</Box>
	);
};
